// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewDraftPinsNote.test.tsx
//
// Over the artwork while the review runs: its pins are drafts, and the
// duplicates among them merge only when it ends. Once it ends with fewer
// findings than she watched arrive, it says so, so the pins that went do not
// read as lost.
import { CircleNotch, X } from "@phosphor-icons/react";
import { IconButton, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Every pin of a running review is a draft. */
export const draftPinTone = (): "draft" => "draft";

type Merged = { from: number; to: number };

/** The most findings seen while the review ran, and what it ended with when that was fewer. */
export function useMergedDrafts(running: boolean, count: number) {
  const peak = useRef(0);
  const wasRunning = useRef(running);
  const [merged, setMerged] = useState<Merged | null>(null);
  useEffect(() => {
    if (running) {
      peak.current = Math.max(peak.current, count);
      setMerged(null);
    } else if (wasRunning.current && peak.current > count) {
      setMerged({ from: peak.current, to: count });
      peak.current = 0;
    }
    wasRunning.current = running;
  }, [count, running]);
  return { merged, dismiss: () => setMerged(null) };
}

const NOTE =
  "absolute right-4 top-3 z-40 flex max-w-[min(26rem,calc(100%-2rem))] items-start gap-2 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-3 py-2 shadow-sm";

export function AskV2ReviewDraftPinsNote({
  running,
  count,
}: {
  running: boolean;
  /** How many findings the review holds now. */
  count: number;
}) {
  const { t } = useTranslation("ui");
  const { merged, dismiss } = useMergedDrafts(running, count);
  if (running) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={NOTE}
        data-testid="ask-v2-review-draft-pins"
      >
        <CircleNotch
          size={16}
          weight="bold"
          className="mt-0.5 shrink-0 animate-spin text-puntt-neutral-gray-9"
          aria-hidden="true"
        />
        <Text as="p" size="1" color="gray">
          {t("askV2.review.draftPins.running", { n: count })}
        </Text>
      </div>
    );
  }
  if (merged == null) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={NOTE}
      data-testid="ask-v2-review-draft-pins-merged"
    >
      <Text as="p" size="1" color="gray">
        {t("askV2.review.draftPins.merged", merged)}
      </Text>
      <IconButton
        size="1"
        variant="ghost"
        color="gray"
        aria-label={t("askV2.review.draftPins.dismiss")}
        data-testid="ask-v2-review-draft-pins-dismiss"
        onClick={dismiss}
      >
        <X size={12} aria-hidden="true" />
      </IconButton>
    </div>
  );
}
